import React, { FormEvent, useState } from 'react'

import { InputField } from '@components/inputField'
import { BirthdayOrderCustomer, BirthdayOrderResponse } from '@facades/birthday'

import {
  ConsentField,
  ConsentList,
  CustomerFields,
  OrderForm,
  OrderSuccess,
  OrderSuccessActions,
  SubmitButton,
  SubmitError,
  TextAreaControl,
  TextAreaField,
} from './birthdayOrderForm.styled'

type CustomerErrors = Partial<Record<keyof BirthdayOrderCustomer, string>>

const initialCustomer: BirthdayOrderCustomer = {
  name: '',
  phone: '',
  email: '',
  note: '',
  voucher: '',
  rulesAccepted: false,
  gdprAccepted: false,
  locale: 'cs',
}

function validate(customer: BirthdayOrderCustomer): CustomerErrors {
  const errors: CustomerErrors = {}
  if (!customer.name.trim()) errors.name = 'Vyplňte jméno a příjmení.'
  if (!/^\+?[0-9]{8,30}$/.test(customer.phone.trim())) {
    errors.phone = 'Zadejte platné telefonní číslo.'
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email.trim())) {
    errors.email = 'Zadejte platnou e-mailovou adresu.'
  }
  if (customer.note.length > 5000) errors.note = 'Zpráva je příliš dlouhá.'
  if (customer.voucher.length > 100) {
    errors.voucher = 'Číslo voucheru je příliš dlouhé.'
  }
  if (!customer.rulesAccepted) {
    errors.rulesAccepted = 'Potvrďte pravidla hry.'
  }
  if (!customer.gdprAccepted) {
    errors.gdprAccepted = 'Potvrďte souhlas se zpracováním údajů.'
  }
  return errors
}

export const BirthdayOrderForm = ({
  isSubmitting,
  submitError,
  result,
  onSubmit,
}: {
  isSubmitting: boolean
  submitError?: string
  result?: BirthdayOrderResponse
  onSubmit: (customer: BirthdayOrderCustomer) => Promise<void>
}) => {
  const [customer, setCustomer] =
    useState<BirthdayOrderCustomer>(initialCustomer)
  const [errors, setErrors] = useState<CustomerErrors>({})

  if (result) {
    return (
      <OrderSuccess role="status">
        <strong>Oslava byla objednána</strong>
        <span>
          {submitError ??
            'Rezervace her i občerstvení byly bezpečně uloženy. Potvrzení jsme poslali e-mailem.'}
        </span>
        <OrderSuccessActions>
          <a href={result.selectionUrl}>Zobrazit uložený výběr</a>
          <code>{result.groupId}</code>
        </OrderSuccessActions>
      </OrderSuccess>
    )
  }

  const setValue = <Key extends keyof BirthdayOrderCustomer>(
    key: Key,
    value: BirthdayOrderCustomer[Key]
  ) => {
    setCustomer((current) => ({ ...current, [key]: value }))
    setErrors((current) => ({ ...current, [key]: undefined }))
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const nextErrors = validate(customer)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) return
    await onSubmit({
      ...customer,
      name: customer.name.trim(),
      phone: customer.phone.trim(),
      email: customer.email.trim(),
      note: customer.note.trim(),
      voucher: customer.voucher.trim(),
    })
  }

  return (
    <OrderForm noValidate onSubmit={submit}>
      <CustomerFields>
        <InputField
          label="Jméno a příjmení"
          value={customer.name}
          error={errors.name}
          required
          autoComplete="name"
          onChange={(event) => setValue('name', event.target.value)}
        />
        <InputField
          label="Telefon"
          type="tel"
          inputMode="tel"
          value={customer.phone}
          error={errors.phone}
          required
          autoComplete="tel"
          placeholder="+420"
          onChange={(event) => setValue('phone', event.target.value)}
        />
        <InputField
          label="E-mail"
          type="email"
          value={customer.email}
          error={errors.email}
          required
          autoComplete="email"
          onChange={(event) => setValue('email', event.target.value)}
        />
        <TextAreaField>
          <label htmlFor="birthday-order-note">Zpráva</label>
          <TextAreaControl
            id="birthday-order-note"
            value={customer.note}
            $invalid={Boolean(errors.note)}
            aria-invalid={Boolean(errors.note)}
            onChange={(event) => setValue('note', event.target.value)}
          />
          {errors.note && <span role="alert">{errors.note}</span>}
        </TextAreaField>
        <InputField
          label="Voucher"
          value={customer.voucher}
          error={errors.voucher}
          onChange={(event) => setValue('voucher', event.target.value)}
        />
      </CustomerFields>
      <ConsentList>
        <ConsentField $invalid={Boolean(errors.rulesAccepted)}>
          <input
            type="checkbox"
            checked={customer.rulesAccepted}
            onChange={(event) =>
              setValue('rulesAccepted', event.target.checked)
            }
          />
          <span>Souhlasím s pravidly hry Questerland.</span>
        </ConsentField>
        <ConsentField $invalid={Boolean(errors.gdprAccepted)}>
          <input
            type="checkbox"
            checked={customer.gdprAccepted}
            onChange={(event) => setValue('gdprAccepted', event.target.checked)}
          />
          <span>Souhlasím se zpracováním osobních údajů.</span>
        </ConsentField>
      </ConsentList>
      {(errors.rulesAccepted || errors.gdprAccepted) && (
        <SubmitError role="alert">
          {errors.rulesAccepted ?? errors.gdprAccepted}
        </SubmitError>
      )}
      {submitError && <SubmitError role="alert">{submitError}</SubmitError>}
      <SubmitButton type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Objednávku ukládáme…' : 'Potvrdit objednávku'}
      </SubmitButton>
    </OrderForm>
  )
}
